import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DatabaseBackup } from 'lucide-react';
import { getCompanies } from '../../../api/General/Company';
import {
  getMigrationStatus,
  migrateCompany,
  retryFailedMigration,
  getOrphanedFiles,
  MigrationStatus,
  MigrationResult,
  OrphanedFilesResult,
} from '../../../api/General/blobMigration';
import { useSignalRConnection } from '../../../hooks';
import { CompanyView } from '../../../models/general';
import { getErrorMessages } from '../../../utils/apiErrors';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Spinner } from '../../../components/ui/Spinner';

import CompanySelector from './components/CompanySelector';
import MigrationStatusPanel from './components/MigrationStatusPanel';
import MigrationProgress from './components/MigrationProgress';
import MigrationResults from './components/MigrationResults';
import MigrationActions from './components/MigrationActions';
import OrphanedFilesPanel from './components/OrphanedFilesPanel';

interface MigrationProgressData {
  current: number;
  total: number;
  migrated: number;
  failed: number;
  skipped: number;
  currentFile: string;
}

export default function BlobMigration() {
  const { t } = useTranslation();
  const companyPrimaryColor = useSelector(
    (state: any) => state.user?.companyPrimaryColor
  );
  const { toast, showToast, dismissToast } = useToast();

  // State
  const [companies, setCompanies] = useState<CompanyView[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyView | null>(null);
  const [status, setStatus] = useState<MigrationStatus | null>(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [progress, setProgress] = useState<MigrationProgressData | null>(null);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [orphanedResult, setOrphanedResult] = useState<OrphanedFilesResult | null>(null);
  const [loadingOrphaned, setLoadingOrphaned] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // SignalR connection
  const { subscribe, subscribeToCompanyGroup, isConnected } = useSignalRConnection();

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Subscribe to SignalR events when company is selected
  useEffect(() => {
    if (!selectedCompany || !isConnected) return;

    // Subscribe to company group for migration updates
    subscribeToCompanyGroup(selectedCompany.id);

    // Subscribe to migration progress events
    const unsubProgress = subscribe('MigrationProgress', (data: MigrationProgressData) => {
      console.log('Migration progress:', data);
      setProgress(data);
    });

    // Subscribe to migration complete events
    const unsubComplete = subscribe('MigrationComplete', (data: MigrationResult) => {
      console.log('Migration complete:', data);
      setResult(data);
      setIsMigrating(false);
      setProgress(null);
      // Refresh status after migration completes
      loadStatus(selectedCompany.id);
    });

    return () => {
      unsubProgress();
      unsubComplete();
    };
  }, [selectedCompany, isConnected, subscribe, subscribeToCompanyGroup]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await getCompanies();
      if (response.payload) {
        setCompanies(response.payload);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
      showToast(
        getErrorMessages(error, t, t('Failed to load companies')).join(', '),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async (companyId: number) => {
    setLoadingStatus(true);
    try {
      const response = await getMigrationStatus(companyId);
      if (response.payload) {
        setStatus(response.payload);
      }
    } catch (error) {
      console.error('Failed to load migration status:', error);
      showToast(
        getErrorMessages(error, t, t('Failed to load migration status')).join(', '),
        'error'
      );
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleCompanyChange = async (company: CompanyView | null) => {
    setSelectedCompany(company);
    setStatus(null);
    setResult(null);
    setOrphanedResult(null);
    setProgress(null);

    if (company) {
      await loadStatus(company.id);
    }
  };

  const handleStartMigration = async () => {
    if (!selectedCompany) return;

    setIsMigrating(true);
    setResult(null);
    setProgress({
      current: 0,
      total: status?.totalPending || 0,
      migrated: 0,
      failed: 0,
      skipped: 0,
      currentFile: '',
    });

    try {
      const response = await migrateCompany(selectedCompany.id);
      if (response.payload) {
        setResult(response.payload);
        showToast(t('Migration completed successfully'), 'success');
      }
    } catch (error) {
      console.error('Migration failed:', error);
      showToast(
        getErrorMessages(error, t, t('Migration failed')).join(', '),
        'error'
      );
    } finally {
      setIsMigrating(false);
      setProgress(null);
      await loadStatus(selectedCompany.id);
    }
  };

  const handleRetryFailed = async () => {
    if (!selectedCompany) return;

    setIsMigrating(true);
    setResult(null);

    try {
      const response = await retryFailedMigration(selectedCompany.id);
      if (response.payload) {
        setResult(response.payload);
        showToast(t('Retry completed'), 'success');
      }
    } catch (error) {
      console.error('Retry failed:', error);
      showToast(
        getErrorMessages(error, t, t('Retry failed')).join(', '),
        'error'
      );
    } finally {
      setIsMigrating(false);
      await loadStatus(selectedCompany.id);
    }
  };

  const handleLoadOrphaned = async () => {
    if (!selectedCompany) return;

    setLoadingOrphaned(true);
    try {
      const response = await getOrphanedFiles(selectedCompany.id);
      if (response.payload) {
        setOrphanedResult(response.payload);
      }
    } catch (error) {
      console.error('Failed to load orphaned files:', error);
      showToast(
        getErrorMessages(error, t, t('Failed to load orphaned files')).join(', '),
        'error'
      );
    } finally {
      setLoadingOrphaned(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PageLayout
      title={t('Blob Storage Migration')}
      description={t('Migrate files from local storage to Azure Blob Storage. Select a company to view migration status and start the migration process.')}
      icon={DatabaseBackup}
      accentColor="slate"
    >
      <div className="flex flex-col gap-6">
        <CompanySelector
          companies={companies}
          selectedCompany={selectedCompany}
          loading={loading}
          onChange={handleCompanyChange}
        />

        {selectedCompany && (
          <>
            <MigrationStatusPanel
              status={status}
              loadingStatus={loadingStatus}
              onRefresh={() => loadStatus(selectedCompany.id)}
            />

            {isMigrating && progress && <MigrationProgress progress={progress} />}

            {result && !isMigrating && <MigrationResults result={result} />}

            <MigrationActions
              isMigrating={isMigrating}
              status={status}
              onStart={handleStartMigration}
              onRetry={handleRetryFailed}
            />

            <OrphanedFilesPanel
              orphanedResult={orphanedResult}
              loadingOrphaned={loadingOrphaned}
              onLoad={handleLoadOrphaned}
            />
          </>
        )}
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
}
